import type { Big, BigSource } from 'big.js';
import { get } from 'lodash-es';
import { defineMessages } from 'react-intl';
import type { ExecutionReport } from '../types/ExecutionReport';
import { PricingMode } from '../types/PricingMode';
import type { Security } from '../types/Security';
import { PricingModeEnum } from '../types/types';
import type { AmountObject } from './notional.types';
import { toBig } from './number';

export const messages = defineMessages({
  impliedVolatility: {
    defaultMessage: 'Implied Volatility',
    id: 'utils.impliedVolatility',
  },
  submittedPrice: {
    defaultMessage: 'Submitted Price',
    id: 'utils.submittedPrice',
  },
  spreadToFixing: {
    defaultMessage: 'Spread to Fixing',
    id: 'utils.spreadToFixing',
  },
  bps: {
    defaultMessage: 'BPS',
    id: 'utils.bps',
  },
  absolute: {
    defaultMessage: 'Absolute',
    id: 'utils.absolute',
  },
});

type MessageKey = keyof typeof messages;

export function getUnitForPriceMode(mode: PricingMode | PricingModeEnum | undefined, defaultUnit?: string): string {
  switch (mode) {
    case PricingMode.ImpliedVolatility:
    case PricingModeEnum.ImpliedVolatility:
      return '%';
    case PricingMode.UnderlyingQuoteCurrency:
    case PricingModeEnum.UnderlyingQuoteCurrency:
      // UI-3085: Default to USD, even though it is theoretically possible to
      // retrieve the currency on the security, it will (almost) always be USD.
      return 'USD';
    case PricingMode.BPS:
    case PricingModeEnum.BPS:
    case PricingModeEnum.SpreadToFixing:
      return 'BPS';
    case PricingModeEnum.Absolute:
    case PricingMode.Default:
    case undefined: {
      return defaultUnit || '';
    }
    default: {
      const _exhaustiveCheck: never = mode;
      return '';
    }
  }
}

export type PricingReferenceEntity = Partial<Pick<ExecutionReport, 'PricingReference' | 'PricingMode' | 'Price'>>;

export function getPricingReference(
  entity: PricingReferenceEntity,
  priceField: string = 'PricingReference' satisfies keyof PricingReferenceEntity
): AmountObject | null {
  const priceBig = toBig(get(entity, priceField));
  if (!entity.PricingMode || !priceBig) {
    return null;
  }

  let priceMultiplier = 1;
  if (entity.PricingMode === PricingModeEnum.SpreadToFixing) {
    priceMultiplier = 10000;
  } else if (entity.PricingMode === PricingModeEnum.ImpliedVolatility) {
    priceMultiplier = 100;
  } else if (entity.PricingMode === PricingModeEnum.BPS) {
    // PricingModeEnum.BPS is already in absolute terms, so no multiplier is needed
    priceMultiplier = 1;
  }

  return {
    value: priceBig.mul(priceMultiplier).toFixed(),
    currency: getUnitForPriceMode(entity.PricingMode),
  };
}

/**
 * Returns whether or not the pricing mode enum means that the pricing is relative. As in, is defined in percentage/bps terms and not currency terms.
 * Just for ease of use, will let you pass an undefined value, which is met by the function returning false.
 */
export function isPricingModeRelative(pricingMode: PricingModeEnum | undefined): boolean {
  if (!pricingMode) {
    return false;
  }

  switch (pricingMode) {
    case PricingModeEnum.ImpliedVolatility:
    case PricingModeEnum.SpreadToFixing:
    case PricingModeEnum.BPS:
      return true;
    case PricingModeEnum.UnderlyingQuoteCurrency:
    case PricingModeEnum.Absolute:
      return false;
    default: {
      const _exhaustiveCheck: never = pricingMode;
      return false;
    }
  }
}

export function prettyPricingMode(pricingMode: NonNullable<ExecutionReport['PricingMode']>): MessageKey | string {
  switch (pricingMode) {
    case PricingModeEnum.ImpliedVolatility:
      return 'impliedVolatility' as MessageKey;
    case PricingModeEnum.UnderlyingQuoteCurrency:
      return 'submittedPrice' as MessageKey;
    case PricingModeEnum.SpreadToFixing:
      return 'spreadToFixing' as MessageKey;
    case PricingModeEnum.BPS:
      return 'bps' as MessageKey;
    case PricingModeEnum.Absolute:
      return 'absolute' as MessageKey;
    default: {
      const _exhaustiveCheck: never = pricingMode;
      return pricingMode;
    }
  }
}

/** Default increment for BPS prices (2 decimal places) */
export const BPS_PRICE_INCREMENT = '0.01';

/** Default display spec for BPS prices - shows 2 decimal places highlighted */
export const BPS_PRICE_DISPLAY_SPEC = 'M.MM';

export type PricingModePriceType =
  /** Prices used to enter or update an order/quote, including submitted prices, bid/offer quotes, and editable price inputs. */
  | 'entryPrice'
  /** Executed prices, including concrete fill prices and average execution prices. */
  | 'fillPrice'
  /** Per-market prices used to enter/update a quote or order, such as a market quote BidPx or OfferPx. */
  | 'marketEntryPrice'
  /** Per-market executed prices, such as market TradedPx. */
  | 'marketFillPrice'
  /** Leg-level entered or updated prices, such as quote leg BidPx or OfferPx. */
  | 'legEntryPrice'
  /** Leg-level executed prices, such as leg TradedPx. */
  | 'legFillPrice';

export interface PricingModeDisplaySpec {
  priceCurrency: string | undefined;
  priceIncrement: string | undefined;
  priceDisplaySpec: string | undefined;
  /** The multiplier for going from data px -> display px (eg 0.0001 -> 1 for StF rendering) */
  displayMultiplier: number | undefined;
  /** Applies the display multiplier and returns a string for passing along (Big.toFixed) */
  toDisplayPrice: (px: BigSource | undefined) => string | undefined;
  /** Same as toDisplayPrice, but returns the big instance (skips the Big.toFixed call) */
  toDisplayPriceBig: (px: BigSource | undefined) => Big | undefined;
}

export type PricingModeDisplaySpecs = Record<PricingModePriceType, PricingModeDisplaySpec>;

const PRICE_DISPLAY_SPECS = {
  BPS: {
    priceCurrency: 'BPS',
    priceIncrement: BPS_PRICE_INCREMENT,
    priceDisplaySpec: BPS_PRICE_DISPLAY_SPEC,
    displayMultiplier: 1,
    toDisplayPrice: px => toBig(px)?.toFixed(),
    toDisplayPriceBig: px => toBig(px),
  },
  SpreadToFixing: {
    priceCurrency: 'BPS',
    priceIncrement: BPS_PRICE_INCREMENT,
    priceDisplaySpec: BPS_PRICE_DISPLAY_SPEC,
    displayMultiplier: 10_000,
    toDisplayPrice: px => toBig(px)?.times(10_000).toFixed(),
    toDisplayPriceBig: px => toBig(px)?.times(10_000),
  },
  ImpliedVolatility: {
    priceCurrency: '%',
    priceIncrement: '0.01',
    priceDisplaySpec: 'M.MM',
    displayMultiplier: 100,
    toDisplayPrice: px => toBig(px)?.times(100).toFixed(),
    toDisplayPriceBig: px => toBig(px)?.times(100),
  },
  Currency: (security: Security | undefined): PricingModeDisplaySpec => ({
    priceCurrency: security?.QuoteCurrency,
    priceIncrement: security?.DefaultPriceIncrement,
    priceDisplaySpec: security?.PriceDisplaySpec,
    displayMultiplier: 1,
    toDisplayPrice: px => toBig(px)?.toFixed(),
    toDisplayPriceBig: px => toBig(px),
  }),
} satisfies {
  BPS: PricingModeDisplaySpec;
  SpreadToFixing: PricingModeDisplaySpec;
  ImpliedVolatility: PricingModeDisplaySpec;
  Currency: (security: Security | undefined) => PricingModeDisplaySpec;
};

/**
 * Returns specifications for how to display the different price fields for an entity's pricing mode and security
 */
export function resolvePricingModeDisplaySpecs({
  pricingMode,
  security,
}: {
  pricingMode: PricingModeEnum | undefined;
  security: Security | undefined;
}): PricingModeDisplaySpecs {
  if (pricingMode === PricingModeEnum.BPS) {
    const currencySpec = PRICE_DISPLAY_SPECS.Currency(security);
    return {
      entryPrice: PRICE_DISPLAY_SPECS.BPS,
      fillPrice: PRICE_DISPLAY_SPECS.BPS,
      marketEntryPrice: currencySpec,
      marketFillPrice: currencySpec,
      legEntryPrice: currencySpec,
      legFillPrice: currencySpec,
    };
  }

  if (pricingMode === PricingModeEnum.SpreadToFixing) {
    const currencySpec = PRICE_DISPLAY_SPECS.Currency(security);
    return {
      entryPrice: PRICE_DISPLAY_SPECS.SpreadToFixing,
      fillPrice: currencySpec,
      marketEntryPrice: PRICE_DISPLAY_SPECS.SpreadToFixing,
      marketFillPrice: currencySpec,
      legEntryPrice: PRICE_DISPLAY_SPECS.SpreadToFixing,
      legFillPrice: currencySpec,
    };
  }

  if (pricingMode === PricingModeEnum.ImpliedVolatility) {
    return {
      entryPrice: PRICE_DISPLAY_SPECS.ImpliedVolatility,
      fillPrice: PRICE_DISPLAY_SPECS.ImpliedVolatility,
      marketEntryPrice: PRICE_DISPLAY_SPECS.ImpliedVolatility,
      marketFillPrice: PRICE_DISPLAY_SPECS.ImpliedVolatility,
      legEntryPrice: PRICE_DISPLAY_SPECS.ImpliedVolatility,
      legFillPrice: PRICE_DISPLAY_SPECS.ImpliedVolatility,
    };
  }

  const currencySpec = PRICE_DISPLAY_SPECS.Currency(security);
  return {
    entryPrice: currencySpec,
    fillPrice: currencySpec,
    marketEntryPrice: currencySpec,
    marketFillPrice: currencySpec,
    legEntryPrice: currencySpec,
    legFillPrice: currencySpec,
  };
}
